<template>
    <div class="min-h-screen py-8">
        <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <!-- Header avec bouton de déconnexion -->
            <div class="flex justify-between items-start mb-12">
                <div class="flex-1 text-center">
                    <h1 class="text-4xl font-bold text-white mb-4 drop-shadow-lg">
                        Bienvenue dans l'éditeur de thèmes de Liview !
                    </h1>
                    <p class="text-xl text-gray-200 drop-shadow-md">
                        Veuillez choisir un thème pour commencer l'édition.
                    </p>
                </div>
                
                <!-- Bouton de déconnexion -->
                <button 
                    @click="handleLogout"
                    class="flex items-center gap-2 px-3 py-2 bg-white/10 hover:bg-white/20 text-gray-200 hover:text-white rounded-lg transition-all duration-200 backdrop-blur-sm border border-white/20 hover:border-white/30"
                    title="Se déconnecter"
                >
                    <logoutIcon />
                    <span class="hidden sm:inline text-sm">Déconnexion</span>
                </button>
            </div>

            <!-- Themes Grid -->
            <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                <!-- Theme Cards -->
                <transition-group 
                    name="theme-card" 
                    tag="div" 
                    class="contents"
                    appear
                >
                    <div 
                        v-for="(theme, index) in themeList" 
                        :key="theme.id" 
                        :style="{ '--i': index }"
                        class="group cursor-pointer bg-white/90 backdrop-blur-sm rounded-xl shadow-2xl hover:shadow-3xl transition-all duration-300 transform hover:-translate-y-2 overflow-hidden"
                    >
                    <!-- Thumbnail -->
                    <div class="aspect-w-16 aspect-h-9 bg-gray-200 relative p-10">
                        <img 
                            :src="theme.fichier_thumbnail.url" 
                            :alt="theme.name + ' thumbnail'" 
                            v-if="theme.fichier_thumbnail"
                            class="w-full h-48 object-contain group-hover:scale-105 transition-transform duration-300"
                        />
                        <div v-else class="w-full h-48 bg-gradient-to-br from-blue-400 to-purple-500 flex items-center justify-center">
                            <svg class="w-16 h-16 text-white opacity-50" fill="currentColor" viewBox="0 0 20 20">
                                <path fill-rule="evenodd" d="M4 3a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V5a2 2 0 00-2-2H4zm12 12H4l4-8 3 6 2-4 3 6z" clip-rule="evenodd" />
                            </svg>
                        </div>
                        <!-- Overlay on hover -->
                        <div class="absolute inset-0 bg-gradient-to-t from-blue-600/80 to-transparent opacity-0 group-hover:opacity-100 transition-all duration-300 flex flex-col gap-2 justify-center items-center justify-center">
                            <div class="button-action opacity-0 group-hover:opacity-100 transition-opacity duration-300" @click="selectThemeAndGoToEditor(index)">
                                <div class="bg-white/95 backdrop-blur-sm text-blue-600 px-6 py-3 rounded-lg font-semibold shadow-lg border border-white/30 hover:bg-white hover:scale-105 hover:shadow-xl transition-all duration-200 cursor-pointer">
                                    Modifier
                                </div>
                            </div>
                            <div class="button-action opacity-0 group-hover:opacity-100 transition-opacity duration-300" @click="duplicateTheme(theme)">
                                <div class="bg-white/95 backdrop-blur-sm text-blue-600 px-6 py-3 rounded-lg font-semibold shadow-lg border border-white/30 hover:bg-white hover:scale-105 hover:shadow-xl transition-all duration-200 cursor-pointer">
                                    Dupliquer
                                </div>
                            </div>
                            <div class="button-action opacity-0 group-hover:opacity-100 transition-opacity duration-300" @click="deleteTheme(theme)">
                                <div class="bg-white/95 backdrop-blur-sm text-red-600 px-6 py-3 rounded-lg font-semibold shadow-lg border border-white/30 hover:bg-white hover:scale-105 hover:shadow-xl transition-all duration-200 cursor-pointer">
                                    Supprimer
                                </div>
                            </div>
                        </div>
                    </div>
                    
                    <!-- Content -->
                    <div class="p-6">
                        <h3 class="text-xl font-semibold text-gray-900 mb-2 group-hover:text-blue-600 transition-colors duration-300">
                            {{ theme.nom }}
                        </h3>
                        <p class="text-gray-600 text-sm leading-relaxed">
                            {{ theme.description }}
                        </p>
                    </div>
                    </div>
                </transition-group>

                <!-- Create New Theme Card -->
                <div 
                    @click="createNewTheme"
                    class="group cursor-pointer bg-white/70 backdrop-blur-sm rounded-xl shadow-2xl hover:shadow-3xl transition-all duration-300 transform hover:-translate-y-2 border-2 border-dashed border-white/40 hover:border-blue-400/80 overflow-hidden"
                >
                    <!-- Add Icon Area -->
                    <div class="aspect-w-16 aspect-h-9 bg-gradient-to-br from-white/20 to-white/10 flex items-center justify-center">
                        <div class="flex flex-col items-center justify-center gap-2 p-10">
                            <div class="w-16 h-16 mx-auto bg-gradient-to-br from-blue-500 to-blue-600 rounded-full flex items-center justify-center group-hover:from-blue-600 group-hover:to-blue-700 transition-all duration-300 shadow-lg">
                                <svg class="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                                </svg>
                            </div>
                            <p class="text-gray-600 group-hover:text-white font-medium transition-colors duration-300 drop-shadow-sm">
                                Nouveau thème
                            </p>
                        </div>
                    </div>
                    
                    <!-- Content -->
                    <div class="p-6">
                        <h3 class="text-xl font-semibold text-gray-800 mb-2 group-hover:text-blue-600 transition-colors duration-300">
                            Créer un nouveau thème
                        </h3>
                        <p class="text-gray-600 text-sm">
                            Commencez avec un thème vide et personnalisez-le selon vos besoins.
                        </p>
                    </div>
                </div>
            </div>
        </div>
        <DeleteConfirmPopup
            :show="showDeleteConfirmPopup"
            :elementName="confirmDeleteElement?.nom"
            @confirm="confirmDeleteTheme"
            @close="showDeleteConfirmPopup = false"
        />
    </div>
</template>

<script>
import { mapGetters, mapMutations, mapActions } from 'vuex';
import * as NAMESPACE_STORE from '../vuexStore/namespaceStore.js';
import * as MUTATES from '../vuexStore/mutations.js';
import * as actions from '../vuexStore/actions.js';
import logoutIcon from '../components/IconComponents/logoutIcon.vue';
import DeleteConfirmPopup from '../components/PopupComponents/DeleteConfirmPopup.vue';

export default {
    name: 'ThemeSelector',
    components: {
        logoutIcon,
        DeleteConfirmPopup
    },
    data() {
        return {
            showDeleteConfirmPopup: false,
            confirmDeleteElement: null
        }
    },
    computed: {
        ...mapGetters(NAMESPACE_STORE.THEME, {
            themeList: "themeList"
        })
    },
    methods: {
        ...mapActions(NAMESPACE_STORE.THEME, {
            deleteThemeAction: actions.DELETE_THEME,
            duplicateThemeAction: actions.DUPLICATE_THEME,
            selectTheme: actions.SELECT_THEME,
        }),
        ...mapActions(NAMESPACE_STORE.USER, {
            logoutAction: "logout"
        }),
        ...mapActions(NAMESPACE_STORE.MESSAGE, {
            addMessage: "addMessage"
        }),
        selectThemeAndGoToEditor(themeId) {
            this.selectTheme(themeId);
            this.$router.push('/editor');
        },
        duplicateTheme(theme) {
            this.duplicateThemeAction(theme.idSoft);
        },
        deleteTheme(theme) {
            this.showDeleteConfirmPopup = true;
            this.confirmDeleteElement = theme;
        },
        confirmDeleteTheme() {
            this.showDeleteConfirmPopup = false;
            this.deleteThemeAction(this.confirmDeleteElement.idSoft).then(() => {
                this.addMessage({
                    message: 'Thème supprimé avec succès',
                    type: 'success'
                });
            }).catch(error => {
                this.addMessage({
                    message: 'Erreur lors de la suppression du thème',
                    type: 'error'
                });
            });
        },
        createNewTheme() {
            // Logique pour créer un nouveau thème
            // Pour l'instant, on redirige vers l'éditeur avec un thème vide
            this.selectTheme(null);
            this.$router.push('/editor');
        },
        handleLogout() {
            this.logoutAction().then(() => {
                this.$router.push('/login');
            }).catch(error => {
                console.error('Erreur lors de la déconnexion:', error);
            });
        }
    }
}
</script>
<style scoped>
.button-action {
    min-width: 50%;
    text-align: center;
}

/* Animations pour les cartes de thèmes */
.theme-card-enter-active {
    transition: all 0.5s ease-out;
}

.theme-card-leave-active {
    transition: all 0.3s ease-in;
}

.theme-card-enter-from {
    opacity: 0;
    transform: translateY(30px) scale(0.9);
}

.theme-card-leave-to {
    opacity: 0;
    transform: translateY(-30px) scale(0.9);
}

.theme-card-move {
    transition: transform 0.4s ease;
}

/* Animation d'apparition initiale */
.theme-card-enter-active {
    transition-delay: calc(var(--i) * 0.1s);
}

/* Animation pour le hover des cartes */
.group:hover .theme-card-enter-active {
    transition-delay: 0s;
}
</style>